import { useId, type ComponentProps, type InputHTMLAttributes } from 'react'

import { ErrorList } from '#app/components/form/error-list.tsx'
import { type ListOfErrors } from '#app/components/form/types.ts'
import { cn } from '#app/utils/misc.tsx'

export interface Props {
	labelProps: ComponentProps<'legend'>
	yesProps: InputHTMLAttributes<HTMLInputElement>
	noProps: InputHTMLAttributes<HTMLInputElement>
	yesLabel?: string
	noLabel?: string
	descriptionProps?: ComponentProps<'p'>
	errors?: ListOfErrors
	className?: string
}

export function YesNoField({
	labelProps,
	yesProps,
	noProps,
	yesLabel = 'Yes',
	noLabel = 'No',
	descriptionProps,
	errors,
	className,
}: Props) {
	const fallbackId = useId()
	const errorId = errors?.length ? `${fallbackId}-error` : undefined

	const {
		className: yesClassName,
		id: providedYesId,
		type: _ignoredYesType,
		...restYesProps
	} = yesProps
	const {
		className: noClassName,
		id: providedNoId,
		type: _ignoredNoType,
		...restNoProps
	} = noProps
	const yesId = providedYesId ?? `${fallbackId}-yes`
	const noId = providedNoId ?? `${fallbackId}-no`
	const legendId = `${fallbackId}-legend`

	return (
		<fieldset
			className={cn('space-y-2.5', className)}
			aria-describedby={errorId}
			aria-invalid={errorId ? true : undefined}
			aria-labelledby={legendId}
		>
			<legend
				id={legendId}
				{...labelProps}
				className={cn('text-sm font-medium', labelProps.className)}
			/>
			{descriptionProps ? (
				<p
					{...descriptionProps}
					className={cn(
						'text-muted-foreground text-xs',
						descriptionProps.className,
					)}
				/>
			) : null}
			<div className="flex flex-wrap gap-4 text-sm">
				<label className="flex items-center gap-2" htmlFor={yesId}>
					<input
						id={yesId}
						className={cn(
							'border-input accent-foreground focus-visible:ring-ring h-4 w-4 border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
							yesClassName,
						)}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						type="radio"
						{...restYesProps}
					/>
					<span>{yesLabel}</span>
				</label>
				<label className="flex items-center gap-2" htmlFor={noId}>
					<input
						id={noId}
						className={cn(
							'border-input accent-foreground focus-visible:ring-ring h-4 w-4 border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
							noClassName,
						)}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						type="radio"
						{...restNoProps}
					/>
					<span>{noLabel}</span>
				</label>
			</div>
			<div className="min-h-[16px]">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</fieldset>
	)
}
